import { DateTime } from "luxon";

import i18n from "../i18n";
import { IInboxConversation, IInboxOrder } from "../models/messagesInbox/IInboxConversation";
import { decodeHtml } from "./decodeHtml";
import { buyerFacingDate } from "./inboxDates";
import { unansweredBuyerMessages } from "./noReplyNeeded";
import { trackingLink } from "./trackingLink";
import { findUkPostcode } from "./ukPostcode";

/**
 * How far along the despatch journey an order is, from what Wilma records (PartData.PickedOn,
 * ShippingLabelDocumentId, ShippedOn). Wilma doesn't know when the courier actually collects, so
 * "label printed, not shipped" is the closest to "packed, waiting for the courier".
 */
export type OrderStage = "not_picked" | "picked" | "awaiting_collection" | "with_courier";

export function orderStage(o: IInboxOrder): OrderStage {
  if (o.shippedAt) return "with_courier";
  if (o.labelPrintedAt) return "awaiting_collection";
  if (o.pickedAt) return "picked";
  return "not_picked";
}

/** Orders this many days past their estimated delivery are flagged to look into. TODO: a setting. */
export const LATE_DELIVERY_DAYS = 2;

/** Whole days past the estimated delivery date (0 when not shipped, no estimate, or not late). */
export function daysPastEstimatedDelivery(o: IInboxOrder, now: DateTime) {
  if (!o.shippedAt || !o.estimatedDelivery) return 0;
  const due = DateTime.fromISO(o.estimatedDelivery, { zone: "utc" }).setZone("Europe/London").endOf("day");
  const late = Math.floor(now.setZone("Europe/London").diff(due, "days").days) + 1;
  return Math.max(0, late);
}

export function isLateDelivery(o: IInboxOrder, now: DateTime) {
  return daysPastEstimatedDelivery(o, now) >= LATE_DELIVERY_DAYS;
}

const london = (iso: string) => DateTime.fromISO(iso, { zone: "utc" }).setZone("Europe/London");
// Written into replies to buyers, so English whatever the UI language (see buyerFacingDate).
const day = (iso: string, now: DateTime) => (london(iso).hasSame(now.setZone("Europe/London"), "day") ? "today" : buyerFacingDate(iso));
/** When it goes out, for a reply. Live orders have no dispatch-by date yet, so they say "soon". */
const dispatchDay = (o: IInboxOrder, now: DateTime) => (o.dispatchBy ? day(o.dispatchBy, now) : "soon");

function addressLines(o: IInboxOrder) {
  return [o.deliveryName, ...(o.deliveryAddress ?? [])].filter(Boolean).join("\n");
}

// Staff-facing text (headlines, steps) follows the UI language; replies and warehouse notes stay English.
const tr = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: "messages", ...options });

const signOff = "\n\nKind regards,\n{shop_name}";

/** The answer to "where's my order?", drafted for the reply box. Nothing is sent. */
export function wheresMyOrderReply(c: IInboxConversation, now: DateTime, includeAddress = false) {
  const o = c.order;
  if (!o) return "";
  const part = c.part?.shortTitle ?? "your part";
  const stage = orderStage(o);
  let body: string;

  if (stage === "with_courier") {
    const late = isLateDelivery(o, now);
    body =
      `Your ${part} went out with ${o.courier ?? "the courier"} on ${buyerFacingDate(o.shippedAt as string)}.` +
      (o.trackingNumber ? ` You can track it here: ${trackingLink(o) ?? o.trackingNumber}` : "") +
      (late
        ? `\n\nI'm sorry it's taking longer than expected – it was due ${day(o.estimatedDelivery as string, now)}. We're chasing ${o.courier ?? "the courier"} now and will update you as soon as we hear back.`
        : o.estimatedDelivery
        ? `\n\nIt's due to arrive ${day(o.estimatedDelivery, now)}.`
        : "");
  } else if (stage === "awaiting_collection") {
    body = `Your ${part} is packed and labelled, and ${o.courier ? `${o.courier} will collect it` : "it will be collected"} ${dispatchDay(o, now)}.`;
  } else if (stage === "picked") {
    body = `Your ${part} has been picked from our warehouse and will go out ${dispatchDay(o, now)}.`;
  } else {
    body = `Your order is with our warehouse team and will be dispatched ${o.dispatchBy ? `by ${dispatchDay(o, now)}` : "soon"}.`;
  }

  const address = includeAddress && !o.isCollection && addressLines(o) ? `\n\nIt's going to:\n${addressLines(o)}` : "";
  return `Hi {buyer_first_name}, thanks for your message. ${body}${address}${signOff}`;
}

export interface IOrderChangeAdvice {
  /** Plain-English summary of what can be done, for the panel. */
  headline: string;
  tone: "green" | "amber" | "red";
  /** What to do, in order. */
  steps: string[];
  reply: string;
  /** Urgent alert for the whole warehouse (#warehouse), when the part is already picked. */
  warehouseNote?: string;
  /** The warehouse alert is the first thing to do (shown as the main button). */
  alertFirst?: boolean;
}

/** Cancellation: what to do depends on how far the order has got (Stuart, 27 Oct 2026). */
export function cancellationAdvice(c: IInboxConversation, now: DateTime): IOrderChangeAdvice | undefined {
  const o = c.order;
  if (!o) return undefined;
  const stage = orderStage(o);
  const tag = c.part ? ` (tag ${c.part.tagNumber}, ${c.part.location})` : "";

  if (stage === "not_picked") {
    return {
      headline: tr("Not picked yet – safe to cancel"),
      tone: "green",
      steps: [tr("Cancel the order"), tr("Put the part back in stock"), tr("Let the buyer know")],
      reply: `Hi {buyer_first_name}, no problem – we've cancelled your order and you'll get a full refund.${signOff}`,
    };
  }
  if (stage === "picked" || stage === "awaiting_collection") {
    return {
      headline: stage === "picked" ? tr("Picked – stop it going out") : tr("Packed – stop it before the courier collects"),
      tone: "amber",
      alertFirst: true,
      steps: [tr("Ask the warehouse to pull it from today's despatch"), tr("Once they confirm, cancel the order"), tr("Let the buyer know")],
      warehouseNote: `URGENT – pull ${c.part?.shortTitle ?? "order " + o.orderNumber}${tag} from today's despatch, the buyer has cancelled.${o.pickedBy ? ` Picked by ${o.pickedBy}.` : ""} Reply once it's back on the shelf.`,
      reply: `Hi {buyer_first_name}, thanks for letting us know. We're stopping your order before it leaves us and will confirm the cancellation shortly.${signOff}`,
    };
  }
  return {
    headline: tr("Already dispatched – can't cancel"),
    tone: "red",
    steps: [tr("Tell the buyer it's on its way"), tr("They can return it once it arrives")],
    reply:
      `Hi {buyer_first_name}, sorry – your order had already left us before your message came in, so we can't cancel it now. ` +
      `It went with ${o.courier ?? "the courier"}${o.estimatedDelivery ? ` and is due ${day(o.estimatedDelivery, now)}` : ""}.` +
      (o.trackingNumber ? ` Tracking: ${trackingLink(o) ?? o.trackingNumber}.` : "") +
      ` If you no longer need it, you can return it once it arrives.${signOff}`,
  };
}

/**
 * Has the buyer actually given the new address? AI reads it out of the message (newAddress); as a
 * check, a UK postcode in anything they've sent since our last reply also counts, so we never
 * ask for an address they've already sent.
 */
export function newAddressGiven(c: IInboxConversation) {
  if (c.newAddress && c.newAddress.length > 0) return { lines: c.newAddress, postcode: undefined };
  const postcode = unansweredBuyerMessages(c)
    .map((m) => findUkPostcode(decodeHtml(m.body)))
    .find(Boolean);
  return postcode ? { lines: undefined, postcode } : undefined;
}

/**
 * How an address change reaches the label, from how Wilma works today: the label takes the address
 * saved in Manage shipping (ShippingService.CreateShipment), so before a label exists, editing it
 * there is enough. Nothing changes the address on eBay or PartPro.
 */
const EDIT_IN_MANAGE_SHIPPING = "Edit the address in Manage shipping – the label will use it";

/**
 * Address change: possible until it's handed to the courier. Time-sensitive: if the buyer hasn't
 * said where it should go, ask straight away, or the warehouse may ship it to the old address.
 */
export function addressChangeAdvice(c: IInboxConversation, now: DateTime): IOrderChangeAdvice | undefined {
  const o = c.order;
  if (!o) return undefined;
  const stage = orderStage(o);
  const current = addressLines(o).replace(/\n/g, ", ");
  const given = newAddressGiven(c);
  const holdNote = `URGENT – hold ${c.part?.shortTitle ?? "order " + o.orderNumber}${c.part ? ` (tag ${c.part.tagNumber})` : ""} – the buyer is changing the delivery address. Don't send it until the address is updated.`;

  if (stage === "with_courier") {
    return {
      headline: tr("Already dispatched – too late to change here"),
      tone: "red",
      steps: [tr("Tell the buyer where it's going"), tr("They can ask the courier to redirect it")],
      reply:
        `Hi {buyer_first_name}, sorry – your order had already left us, so we can't change the address now. It's going to ${current || "the address on the order"}` +
        ` with ${o.courier ?? "the courier"}${o.trackingNumber ? ` (tracking ${trackingLink(o) ?? o.trackingNumber})` : ""}, and they may be able to redirect it for you.${signOff}`,
    };
  }

  // Label printed: it may already be on the courier's van. Ask the warehouse first - only they
  // know if it's gone and whether it can still be stopped (Stuart, 27 Oct 2026).
  if (stage === "awaiting_collection") {
    const newAddress = given?.lines ? ` to ${given.lines.join(", ")}` : "";
    return {
      headline: tr("Label printed – check with the warehouse first"),
      tone: "red",
      alertFirst: true,
      steps: [
        tr("Alert the warehouse: has it gone out, and can they stop it?"),
        ...(given ? [] : [tr("Ask the buyer for the full new address (drafted)")]),
        tr("If they've stopped it: cancel the label in Manage shipping (needs the PIN), edit the address, create a new label"),
        tr("If it's already gone: tell the buyer it's on its way to the old address"),
      ],
      warehouseNote:
        `URGENT – has ${c.part?.shortTitle ?? "order " + o.orderNumber}${c.part ? ` (tag ${c.part.tagNumber}, order ${o.orderNumber})` : ""} gone out yet? ` +
        `The buyer needs it sent to a different address. If it's still here, please stop it and reply – the label needs cancelling and reprinting.`,
      reply: given
        ? `Hi {buyer_first_name}, thanks for letting us know. Your order was about to go out, so we're checking with our warehouse whether it's already left. If it hasn't, we'll send it${newAddress} instead – we'll confirm shortly.${signOff}`
        : `Hi {buyer_first_name}, thanks for letting us know. Your order was about to go out, so we're checking with our warehouse whether it's already left. ` +
          `Please reply with the full new address, including the postcode, as soon as you can so we can update it if we catch it in time.${signOff}`,
    };
  }

  if (!given) {
    return {
      headline: tr("No new address given – ask for it now"),
      tone: "red",
      steps: [
        tr("Ask the buyer for the full new address (drafted)"),
        ...(stage === "not_picked" ? [] : [tr("Tell the warehouse to hold it")]),
        tr(EDIT_IN_MANAGE_SHIPPING),
      ],
      warehouseNote: stage === "not_picked" ? undefined : holdNote,
      reply:
        `Hi {buyer_first_name}, no problem. Please can you reply with the full address you'd like it sent to, including the postcode? ` +
        `It's time-sensitive – your order is due to go out ${dispatchDay(o, now)}, so the sooner we have it, the sooner we can update it before it leaves us.${signOff}`,
    };
  }

  const newAddress = given.lines ? given.lines.join(", ") : undefined;
  return {
    headline: stage === "not_picked" ? tr("New address given – update it") : tr("New address given – update it before it goes"),
    tone: stage === "not_picked" ? "green" : "amber",
    steps: [
      given.lines ? tr("Check the new address") : tr("Check the full address in their message (postcode {{postcode}})", { postcode: given.postcode }),
      ...(stage === "not_picked" ? [] : [tr("Tell the warehouse to hold it")]),
      tr(EDIT_IN_MANAGE_SHIPPING),
      tr("Confirm it to the buyer"),
    ],
    warehouseNote: stage === "not_picked" ? undefined : holdNote,
    reply: `Hi {buyer_first_name}, no problem – we've updated your delivery address${newAddress ? ` to ${newAddress}` : ""} and your order will go out ${dispatchDay(o, now)}.${signOff}`,
  };
}

/** Any other change to the order (wrong side, add an item, swap a part). */
export function amendmentAdvice(c: IInboxConversation, now: DateTime): IOrderChangeAdvice | undefined {
  const o = c.order;
  if (!o) return undefined;
  const stage = orderStage(o);
  if (stage === "with_courier") {
    return {
      headline: tr("Already dispatched – can't change the order"),
      tone: "red",
      steps: [tr("Tell the buyer it's on its way"), tr("Sort the change as a return or a new order")],
      reply: `Hi {buyer_first_name}, sorry – your order had already left us, so we can't change it now. It's due ${o.estimatedDelivery ? day(o.estimatedDelivery, now) : "shortly"}. Once it arrives we can sort this out for you.${signOff}`,
    };
  }
  return {
    headline: stage === "not_picked" ? tr("Not dispatched – can still change") : tr("Not dispatched yet – hold it while you check"),
    tone: stage === "not_picked" ? "green" : "amber",
    steps: [tr("Check the change is possible (stock, price, postage)"), tr("Update the order"), tr("Confirm it to the buyer")],
    warehouseNote:
      stage === "not_picked" ? undefined : `URGENT – hold ${c.part?.shortTitle ?? "order " + o.orderNumber}${c.part ? ` (tag ${c.part.tagNumber})` : ""}, the buyer wants to change their order. Don't send it yet.`,
    reply: `Hi {buyer_first_name}, thanks for letting us know. Your order hasn't gone out yet, so we're checking the change now and will come back to you shortly.${signOff}`,
  };
}
